import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { ProdutoService } from '../../../../../shared/services/produto.service';

export interface ProdutoConsistirDialogData {
  transacaoId: number;
  emissao: string;        // yyyy-MM-dd
  transacaoLabel?: string;
}

/**
 * Diálogo de "Consistir Lançamentos" (aba Movimentação) — mesma semântica do desktop:
 * lista os documentos da transação a partir da emissão e reprocessa documento a documento,
 * mostrando barra de progresso, log ao vivo e permitindo cancelar.
 */
@Component({
  selector: 'app-produto-consistir-dialog',
  templateUrl: './produto-consistir-dialog.component.html',
  styleUrls: ['./produto-consistir-dialog.component.scss'],
})
export class ProdutoConsistirDialogComponent implements OnInit {

  titulo = 'Preparando...';
  progress = 0;          // 0..1 (multiplicado por 100 no template)
  log: string[] = [];
  running = false;
  finished = false;
  private cancelado = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProdutoConsistirDialogData,
    public dialogRef: MatDialogRef<ProdutoConsistirDialogComponent>,
    private produtoService: ProdutoService,
  ) {
    this.dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.executar();
  }

  private append(msg: string): void {
    this.log.push(msg);
  }

  private erroMsg(e: any): string {
    return e?.error?.status || e?.error?.message || e?.message || `${e}`;
  }

  private async executar(): Promise<void> {
    this.running = true;
    try {
      const doctos = (await this.produtoService
        .consistirListar(this.data.transacaoId, this.data.emissao).toPromise()) || [];
      const total = doctos.length;
      this.append(`Encontrados ${total} documento(s) para transação `
        + `${this.data.transacaoLabel || this.data.transacaoId} a partir de `
        + `${moment(this.data.emissao).format('DD/MM/YYYY')}.`);
      this.titulo = total === 0 ? 'Nada a consistir.' : `${total} documento(s) a consistir.`;
      if (total === 0) {
        this.progress = 1;
      }

      let processados = 0;
      let itensTotal = 0;
      for (const doc of doctos) {
        if (this.cancelado) {
          break;
        }
        try {
          const res = await this.produtoService.consistirControle(doc.controle).toPromise();
          const itens = res?.itens || 0;
          itensTotal += itens;
          processados++;
          this.progress = processados / total;
          this.titulo = `Consistindo ${processados}/${total}...`;
          this.append(`Documento ${processados}/${total} (controle ${doc.controle}, `
            + `emissão ${moment(doc.emissao).format('DD/MM/YYYY')}) — ${itens} item(ns).`);
        } catch (e) {
          this.append(`ERRO no controle ${doc.controle}: ${this.erroMsg(e)}`);
        }
      }

      if (this.cancelado) {
        this.titulo = 'Cancelado.';
        this.append(`CANCELADO após ${processados}/${total} documento(s), ${itensTotal} item(ns).`);
      } else {
        this.progress = 1;
        this.titulo = 'Concluído.';
        this.append(`CONCLUÍDO: ${processados} documento(s), ${itensTotal} item(ns) reprocessado(s).`);
      }
    } catch (e) {
      this.titulo = 'Erro.';
      this.append(`ERRO: ${this.erroMsg(e)}`);
    } finally {
      this.running = false;
      this.finished = true;
    }
  }

  cancelar(): void {
    this.cancelado = true;
    this.titulo = 'Cancelando...';
  }

  fechar(): void {
    // devolve true quando terminou, para o chamador recarregar a lista de movimentação
    this.dialogRef.close(this.finished);
  }
}
